import { motion } from 'framer-motion';
import type { CSSProperties, FunctionComponent } from 'react';
import { useMemo } from 'react';
import { createRoot } from 'react-dom/client';

export type ClickPosition = { readonly x: number; readonly y: number };

const PALETTE: readonly string[] = ['#16a34a', '#eab308', '#ec4899', '#a78bfa', '#3b82f6', '#f97316'];

const BURST_LIFETIME_MS = 1200;

export const TASTEFUL_CONFETTI_SETTLE_MS = 500;

type BurstPiece = {
    readonly i: number;
    readonly dx: number;
    readonly dy: number;
    readonly rot: number;
    readonly color: string;
    readonly w: number;
    readonly h: number;
    readonly shape: 'circle' | 'rect';
    readonly delay: number;
};

const Burst: FunctionComponent<{ readonly anchor: ClickPosition }> = ({ anchor }) => {
    const pieces = useMemo<readonly BurstPiece[]>(
        () =>
            Array.from({ length: 14 }, (_, i): BurstPiece => {
                const angleDeg = -120 + Math.random() * 60;
                const angle = (angleDeg * Math.PI) / 180;
                const speed = 70 + Math.random() * 50;
                return {
                    i,
                    dx: Math.cos(angle) * speed,
                    dy: Math.sin(angle) * speed - 24,
                    rot: (Math.random() - 0.5) * 360,
                    color: PALETTE[i % PALETTE.length] ?? '#16a34a',
                    w: 4 + Math.random() * 3,
                    h: 5 + Math.random() * 4,
                    shape: Math.random() > 0.6 ? 'circle' : 'rect',
                    delay: Math.random() * 0.04,
                };
            }),
        [],
    );

    const containerStyle: CSSProperties = {
        position: 'absolute',
        left: anchor.x,
        top: anchor.y,
        width: 0,
        height: 0,
    };

    return (
        <div style={containerStyle}>
            {pieces.map((p) => (
                <motion.div
                    key={p.i}
                    initial={{ x: 0, y: 0, opacity: 0, rotate: 0, scale: 0.5 }}
                    animate={{
                        x: p.dx,
                        y: p.dy + 70,
                        opacity: [0, 1, 1, 0],
                        rotate: p.rot,
                        scale: 1,
                    }}
                    transition={{
                        duration: 1.0,
                        delay: p.delay,
                        ease: [0.15, 0.7, 0.3, 1],
                        times: [0, 0.15, 0.7, 1],
                    }}
                    style={{
                        position: 'absolute',
                        left: -p.w / 2,
                        top: -p.h / 2,
                        width: p.w,
                        height: p.shape === 'circle' ? p.w : p.h,
                        background: p.color,
                        borderRadius: p.shape === 'circle' ? 999 : 1.5,
                    }}
                />
            ))}
        </div>
    );
};

export const fireTastefulConfetti = (anchor: ClickPosition): void => {
    if (typeof document === 'undefined') return;

    const host = document.createElement('div');
    host.setAttribute('aria-hidden', 'true');
    host.style.position = 'fixed';
    host.style.inset = '0';
    host.style.pointerEvents = 'none';
    host.style.overflow = 'hidden';
    host.style.zIndex = '9999';
    document.body.appendChild(host);

    const root = createRoot(host);
    root.render(<Burst anchor={anchor} />);

    window.setTimeout(() => {
        root.unmount();
        host.remove();
    }, BURST_LIFETIME_MS);
};

export const fireTastefulConfettiAndSettle = async (event: {
    readonly clientX: number;
    readonly clientY: number;
}): Promise<void> => {
    fireTastefulConfetti({ x: event.clientX, y: event.clientY });
    await new Promise<void>((resolve) => {
        window.setTimeout(resolve, TASTEFUL_CONFETTI_SETTLE_MS);
    });
};
